import { FormEvent, useEffect, useRef } from "react";
import { DropdownV2, FormCardV2, FormGridV2, InputV2 } from "../FormControlsV2";
import { FormActions } from "./FormActions";
import { AreaFormProps, AreaFormValueV2 } from "./types";
import { createAutomaticAreaInitials, isDisabled, parseCoords, stringifyCoords } from "./utils";
import { useTranslation } from "react-i18next";

export function AreaFormV2({
  value,
  mode = "create",
  errors = {},
  disabledFields = [],
  cardTitle,
  cardDescription,
  showCardHeader = true,
  farms,
  managers,
  dayOptions,
  initialsPrefix,
  autoGenerateInitials = true,
  availableArea,
  mapSlot,
  headerActions,
  showActions = true,
  onChange,
  onBlur,
  onSubmit,
  onCancel,
  onFarmChange,
}: AreaFormProps) {
  const { t } = useTranslation();
  const previousAutoInitials = useRef("");

  function update(partial: Partial<AreaFormValueV2>) {
    onChange({ ...value, ...partial });
  }

  function handleNameChange(name: string) {
    if (!autoGenerateInitials) {
      update({ name });
      return;
    }

    const nextInitials = createAutomaticAreaInitials(name);
    const currentMatchesAutoInitials = !value.initials || value.initials === previousAutoInitials.current;
    previousAutoInitials.current = nextInitials;
    update({
      name,
      initials: currentMatchesAutoInitials ? nextInitials : value.initials,
    });
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    onSubmit && onSubmit(value);
  }

  useEffect(() => {
    if (
      mode !== "readonly" &&
      managers.length === 1 &&
      !value.manager_user_id &&
      !disabledFields.includes("manager_user_id")
    ) {
      onChange({ ...value, manager_user_id: managers[0] });
    }
  }, [disabledFields, managers, mode, onChange, value]);

  return (
    <form className="ov2-property-form" onBlur={onBlur} onSubmit={handleSubmit}>
      <FormCardV2
        title={showCardHeader ? cardTitle || t("formsV2.area") : undefined}
        description={showCardHeader ? cardDescription : undefined}
        actions={headerActions}
      >
        <div className="ov2-property-form__layout">
          <FormGridV2>
            <DropdownV2
              name="farm"
              label={t("formsV2.farm")}
              value={value.farm}
              options={farms}
              searchable
              disabled={isDisabled("farm", mode, disabledFields)}
              error={errors.farm}
              onChange={(farm) => {
                onFarmChange && onFarmChange(farm);
                update({ farm });
              }}
            />
            <InputV2
              name="name"
              label={t("formsV2.areaName")}
              value={value.name}
              disabled={isDisabled("name", mode, disabledFields)}
              error={errors.name}
              onChange={handleNameChange}
            />
            <InputV2
              name="initials"
              label={t("formsV2.initials")}
              prefix={initialsPrefix}
              value={value.initials}
              maxLength={3}
              disabled={isDisabled("initials", mode, disabledFields)}
              error={errors.initials}
              onChange={(initials) => update({ initials: initials.toUpperCase() })}
            />
            <DropdownV2
              name="indoor_grazing_time"
              label={t("formsV2.maxTimeWithCattle")}
              value={value.indoor_grazing_time}
              options={dayOptions}
              disabled={isDisabled("indoor_grazing_time", mode, disabledFields)}
              error={errors.indoor_grazing_time}
              onChange={(indoor_grazing_time) => update({ indoor_grazing_time })}
            />
            <DropdownV2
              name="outdoor_grazing_time"
              label={t("formsV2.maxTimeWithoutCattle")}
              value={value.outdoor_grazing_time}
              options={dayOptions}
              disabled={isDisabled("outdoor_grazing_time", mode, disabledFields)}
              error={errors.outdoor_grazing_time}
              onChange={(outdoor_grazing_time) => update({ outdoor_grazing_time })}
            />
            <DropdownV2
              name="manager_user_id"
              label={t("formsV2.areaManager")}
              value={value.manager_user_id}
              options={managers}
              searchable
              disabled={isDisabled("manager_user_id", mode, disabledFields)}
              error={errors.manager_user_id}
              onChange={(manager_user_id) => update({ manager_user_id })}
            />
          </FormGridV2>

          <div className="ov2-property-form__map">
            {mapSlot || (
              <InputV2
                name="map_coords"
                label={t("formsV2.mapCoordinates")}
                type="textarea"
                value={stringifyCoords(value.map_coords)}
                disabled={isDisabled("map_coords", mode, disabledFields)}
                error={errors.map_coords}
                onChange={(coords) => update({ map_coords: parseCoords(coords) })}
              />
            )}
            <div className="ov2-property-acreage-tag">
              <span>{t("formsV2.usefulArea")}</span>
              <strong>{value.acreage || "0"} ha</strong>
            </div>
            {errors.acreage && (
              <small className="ov2-field__error animation__fadeIn">{errors.acreage}</small>
            )}
          </div>
        </div>
      </FormCardV2>

      {showActions && <FormActions mode={mode} onCancel={onCancel} />}
    </form>
  );
}
